import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Typography,
  Button,
  FormHelperText,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Divider,
  Alert,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

// ============================================================================
// TYPESCRIPT INTERFACES
// ============================================================================

interface Step {
  uid: string;
  _id?: string;
  title: string;
}

interface Errors {
  [key: string]: string;
}

interface WebhookConfig {
  authenticationType: string | null;
  authenticationKey: string | null;
  endPointUrl: string | null;
}

interface IntegrationConfig {
  _id: string;
  teamUid: string;
  stepUid: string[];
  integrationType: 'webhook' | 'smartsheet';
  configFields: any[];
  allTransaction?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface TeamData {
  teamUid: string;
  webhook: WebhookConfig;
  [key: string]: any;
}

interface IntegrationFormProps {
  selectedConfig: IntegrationConfig | null;
  stepOptions: Step[];
  teamData: TeamData | undefined;
  teamUid: string;
  onSubmit: (payload: any) => Promise<void>;
  onCancel: () => void;
  onTeamFormSubmit?: (formData: any, callback: (success: boolean) => void) => void;
  showToast: (message: string, type: 'success' | 'error') => void;
  theme: any;
}

// ============================================================================
// INTEGRATION FORM COMPONENT
// ============================================================================

const IntegrationForm: React.FC<IntegrationFormProps> = ({
  selectedConfig,
  stepOptions,
  teamData,
  teamUid,
  onSubmit,
  onCancel,
  onTeamFormSubmit,
  showToast,
  theme
}) => {
  // Permission Check
  const hasIntegrationPermission = useHasPermission(PERMISSIONS.ADD_EDIT_INTEGRATION_CONFIG);
console.log("hasIntegrationPermission",hasIntegrationPermission);
  // State Management
  const [selectedSteps, setSelectedSteps] = useState<string[]>([]);
  const [integrationType, setIntegrationType] = useState<string>('');
  const [dynamicFields, setDynamicFields] = useState<any[]>([{ key: '', value: '' }]);
  const [allTransaction, setAllTransaction] = useState<boolean>(true);
  const [webhookConfig, setWebhookConfig] = useState<WebhookConfig>({
    authenticationType: null,
    authenticationKey: null,
    endPointUrl: null
  });
  const [webhookConfigExists, setWebhookConfigExists] = useState(false);
  const [webhookConfigChanged, setWebhookConfigChanged] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load team webhook config on mount
  useEffect(() => {
    if (teamData?.webhook) {
      const hasWebhookData =
        teamData.webhook.authenticationType ||
        teamData.webhook.authenticationKey ||
        teamData.webhook.endPointUrl;

      if (hasWebhookData) {
        setWebhookConfig({
          authenticationType: teamData.webhook.authenticationType || null,
          authenticationKey: teamData.webhook.authenticationKey || null,
          endPointUrl: teamData.webhook.endPointUrl || null
        });
        setWebhookConfigExists(true);
      }
    }
  }, [teamData]);

  // Load selected config for editing
  useEffect(() => {
    if (selectedConfig) {
      if (selectedConfig.stepUid) {
        setSelectedSteps(Array.isArray(selectedConfig.stepUid) ? selectedConfig.stepUid : [selectedConfig.stepUid]);
      }

      if (selectedConfig.integrationType) {
        setIntegrationType(selectedConfig.integrationType);
      }

      if (selectedConfig.configFields && Array.isArray(selectedConfig.configFields)) {
        if (selectedConfig.configFields.length > 0) {
          setDynamicFields(selectedConfig.configFields);
        }
      }

      // Load allTransaction value, default to true if not present
      setAllTransaction(selectedConfig.allTransaction !== undefined ? selectedConfig.allTransaction : true);
    } else {
      resetForm();
    }
  }, [selectedConfig]);

  const getFieldError = (field: string): string => {
    return errors[field] || '';
  };

  const handleStepChange = (event: any): void => {
    const value = event.target.value;
    setSelectedSteps(typeof value === 'string' ? value.split(',') : value);
    setErrors({ ...errors, step: '' });
  };

  const handleIntegrationTypeChange = (value: string): void => {
    setIntegrationType(value);
    setErrors({ ...errors, integrationType: '' });

    if (value === 'webhook') {
      setDynamicFields([]);
    } else if (value === 'smartsheet') {
      setDynamicFields([{ 'API Key': '' }]);
    } else {
      setDynamicFields([{ key: '', value: '' }]);
    }
  };

  const handleWebhookConfigChange = (field: keyof WebhookConfig, value: string): void => {
    setWebhookConfig(prev => ({
      ...prev,
      [field]: value || null
    }));
    setWebhookConfigChanged(true);
    setErrors({ ...errors, [field]: '' });
  };

  const handleDynamicFieldChange = (index: number, fieldKey: string, value: string): void => {
    const updated = [...dynamicFields];
    updated[index][fieldKey] = value;
    setDynamicFields(updated);
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};

    if (selectedSteps.length === 0) {
      newErrors.step = 'Please select at least one step';
    }

    if (!integrationType) {
      newErrors.integrationType = 'Please select an integration type';
    }

    // Validate webhook config if webhook type is selected
    if (integrationType === 'webhook') {
      if (!webhookConfig.authenticationType) {
        newErrors.authenticationType = 'Authentication Type is required';
      }
      if (!webhookConfig.authenticationKey) {
        newErrors.authenticationKey = 'Authentication Key is required';
      }
      if (!webhookConfig.endPointUrl) {
        newErrors.endPointUrl = 'Endpoint URL is required';
      }
    }

    // Validate dynamic fields for other integration types
    if (integrationType !== 'webhook') {
      dynamicFields.forEach((field, i) => {
        const keys = Object.keys(field);
        keys.forEach(key => {
          if (!field[key]) {
            newErrors[`${key}-${i}`] = `${key} is required`;
          }
        });
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      // If webhook type and config has changed, save webhook config to team first
      if (integrationType === 'webhook' && webhookConfigChanged && onTeamFormSubmit) {
        const teamPayload = {
          ...teamData,
          webhook: {
            authenticationType: webhookConfig.authenticationType,
            authenticationKey: webhookConfig.authenticationKey,
            endPointUrl: webhookConfig.endPointUrl
          }
        };

        // Update team with webhook config
        await new Promise<void>((resolve, reject) => {
          onTeamFormSubmit(teamPayload, (success: boolean) => {
            if (success) {
              setWebhookConfigExists(true);
              setWebhookConfigChanged(false);
              resolve();
            } else {
              reject(new Error('Failed to update team webhook config'));
            }
          });
        });

        showToast('Webhook configuration saved to team', 'success');
      }

      // Save step-specific integration config
      const payload = {
        teamUid,
        stepUid: selectedSteps,
        integrationType,
        configFields: integrationType === 'webhook' ? [] : dynamicFields,
        allTransaction
      };

      await onSubmit(payload);
    } catch (error: any) {
      showToast(error?.message || 'An error occurred while saving', 'error');
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedSteps([]);
    setIntegrationType('');
    setDynamicFields([{ key: '', value: '' }]);
    setAllTransaction(true);
    setErrors({});
    setWebhookConfigChanged(false);
  };

  return (
    <Box sx={{ p: 4, maxWidth: 700, mx: 'auto' }}>
      {/* Back Button */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button
          onClick={onCancel}
          startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: 'secondary.dark',
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'secondary.dark',
              textDecoration: 'underline'
            }
          }}
        >
          Back to Integrations
        </Button>
      </Box>

      <Typography
        variant="h5"
        sx={{
          mb: 1,
          fontWeight: 600,
          color: '#1a1a1a',
          letterSpacing: '-0.5px'
        }}
      >
        {selectedConfig ? 'Edit Integration' : 'Add Integration'}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 4,
          color: '#64748b',
          fontSize: '0.9rem'
        }}
      >
        Configure your integration settings and trigger conditions
      </Typography>

      {/* Multi-Select Step Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1.5,
            fontWeight: 600,
            color: '#334155',
            fontSize: '0.875rem'
          }}
        >
          On Completion of Step *
        </Typography>
        <FormControl
          fullWidth
          size="small"
          error={!!getFieldError('step')}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: '#f8fafc',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: '#f1f5f9',
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
              }
            }
          }}
        >
          <Select
            multiple
            value={selectedSteps}
            onChange={handleStepChange}
            input={<OutlinedInput />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const step = stepOptions.find(s => s.uid === value || s._id === value);
                  return (
                    <Chip
                      key={value}
                      label={step?.title || value}
                      size="small"
                      sx={{
                        backgroundColor: 'secondary.dark',
                        color: 'white',
                        fontWeight: 500,
                        height: '24px',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            color: 'white'
                          }
                        }
                      }}
                    />
                  );
                })}
              </Box>
            )}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em style={{ color: '#94a3b8' }}>Select Steps</em>
            </MenuItem>
            {stepOptions.map((step) => {
              const stepId = step.uid || step._id || '';
              return (
              <MenuItem key={stepId} value={stepId}>
                <Checkbox
                  checked={selectedSteps.indexOf(stepId) > -1}
                  sx={{
                    color: '#cbd5e1',
                    '&.Mui-checked': {
                      color: 'secondary.dark',
                    }
                  }}
                />
                <ListItemText primary={step.title} />
              </MenuItem>
            );
            })}
          </Select>
          {getFieldError('step') && (
            <FormHelperText sx={{ ml: 0, mt: 1, color: '#ef4444' }}>
              {getFieldError('step')}
            </FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* All Transaction Checkbox */}
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={allTransaction}
              onChange={(e) => setAllTransaction(e.target.checked)}
              sx={{
                color: '#cbd5e1',
                '&.Mui-checked': {
                  color: 'secondary.dark',
                }
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontWeight: 500,
                color: '#334155',
                fontSize: '0.875rem'
              }}
            >
              All Transactions
            </Typography>
          }
          sx={{
            ml: 0,
            p: 0,
            
          }}
        />
        
      </Box>

      {/* Integration Type */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1.5,
            fontWeight: 600,
            color: '#334155',
            fontSize: '0.875rem'
          }}
        >
          Integration Type *
        </Typography>
        <FormControl
          fullWidth
          size="small"
          error={!!getFieldError('integrationType')}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: '#f8fafc',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: '#f1f5f9',
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
              }
            }
          }}
        >
          <Select
            value={integrationType}
            onChange={(e) => handleIntegrationTypeChange(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em style={{ color: '#94a3b8' }}>Select Integration Type</em>
            </MenuItem>
            <MenuItem value="webhook">Webhook</MenuItem>
            {/* <MenuItem value="smartsheet">Smart Sheet</MenuItem> */}
          </Select>
          {getFieldError('integrationType') && (
            <FormHelperText sx={{ ml: 0, mt: 1, color: '#ef4444' }}>
              {getFieldError('integrationType')}
            </FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* Webhook Configuration (Team-level) */}
      {integrationType === 'webhook' && (
        <Box sx={{ mb: 4 }}>
          <Divider sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontWeight: 600,
                px: 2
              }}
            >
              Webhook Configuration (Team Level)
            </Typography>
          </Divider>

          {webhookConfigExists && !webhookConfigChanged && (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                color:"secondary.dark",
                borderRadius: '10px',
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              Webhook credentials are already configured for this team. You can update them below if needed.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small"
              label="Authentication Type *"
              value={webhookConfig.authenticationType || ''}
              onChange={(e) => handleWebhookConfigChange('authenticationType', e.target.value)}
              error={!!getFieldError('authenticationType')}
              helperText={getFieldError('authenticationType')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  }
                }
              }}
              placeholder="e.g., Bearer, API Key, Basic"
            />

            <TextField
              size="small"
              label="Authentication Key *"
              value={webhookConfig.authenticationKey || ''}
              onChange={(e) => handleWebhookConfigChange('authenticationKey', e.target.value)}
              error={!!getFieldError('authenticationKey')}
              helperText={getFieldError('authenticationKey')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  }
                }
              }}
              type="text"
              placeholder="Enter your authentication key"
            />

            <TextField
              size="small"
              label="Endpoint URL *"
              value={webhookConfig.endPointUrl || ''}
              onChange={(e) => handleWebhookConfigChange('endPointUrl', e.target.value)}
              error={!!getFieldError('endPointUrl')}
              helperText={getFieldError('endPointUrl')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                  }
                }
              }}
              placeholder="https://api.example.com/webhook"
            />
          </Box>

          {webhookConfigChanged && (
            <Alert
              severity="warning"
              sx={{
                mt: 2,
                borderRadius: '10px',
                '& .MuiAlert-message': {
                  fontSize: '0.875rem'
                }
              }}
            >
              Webhook configuration will be updated for all webhook integrations in this team.
            </Alert>
          )}
        </Box>
      )}

      {/* Dynamic Fields for Other Integration Types */}
      {integrationType && integrationType !== 'webhook' && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#334155',
              fontSize: '0.875rem'
            }}
          >
            Configuration Fields
          </Typography>

          {dynamicFields.map((field, index) => {
            const fieldKeys = Object.keys(field);
            return (
              <Box key={index}>
                {fieldKeys.map((fieldKey) => (
                  <Box
                    key={fieldKey}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 2,
                      alignItems: 'flex-start'
                    }}
                  >
                    <TextField
                      size="small"
                      label="Key"
                      value={fieldKey}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: '#f8fafc',
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#fff',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          }
                        }
                      }}
                      disabled
                    />
                    <TextField
                      size="small"
                      label="Value"
                      value={field[fieldKey]}
                      onChange={(e) => handleDynamicFieldChange(index, fieldKey, e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          backgroundColor: '#f8fafc',
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#fff',
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                          }
                        }
                      }}
                      error={!!getFieldError(`${fieldKey}-${index}`)}
                      helperText={getFieldError(`${fieldKey}-${index}`)}
                    />
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 4,
          pt: 3,
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            color: '#64748b',
            px: 3,
            '&:hover': {
              borderColor: '#cbd5e1',
              backgroundColor: '#f8fafc',
            }
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!hasIntegrationPermission}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            backgroundColor: 'secondary.dark',
            px: 3,
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              backgroundColor: 'secondary.dark',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            },
            '&:disabled': {
              backgroundColor: '#cbd5e1',
            }
          }}
        >
          {isSubmitting ? 'Saving...' : selectedConfig ? 'Update Configuration' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
};

export default IntegrationForm;